<template>
  <div class="p-6">
    <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
      {{ type === 'ga' ? 'Connect Google Analytics' : 'Connect Search Console' }}
    </h2>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
      Select a {{ type === 'ga' ? 'GA4 property' : 'Search Console site' }} to connect to <strong>{{ websiteName }}</strong>.
    </p>

    <div v-if="options.length === 0" class="text-center py-8">
      <p class="text-sm text-gray-500 dark:text-gray-400">
        No {{ type === 'ga' ? 'analytics properties' : 'search console sites' }} found.
      </p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-2">
        Go to your profile and click "Refresh" on a connected Google account first.
      </p>
    </div>

    <div v-else class="space-y-3">
      <input
        v-if="options.length > 5"
        v-model="searchQuery"
        type="text"
        :placeholder="`Filter ${type === 'ga' ? 'properties' : 'sites'}...`"
        class="w-full text-sm border-2 border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:ring-primary-500 focus:border-primary-500">
      <div class="space-y-2 max-h-[60vh] overflow-y-auto">
        <label
          v-for="option in filteredOptions"
          :key="option.id"
          class="flex items-center p-3 border rounded-lg cursor-pointer transition-colors"
          :class="selectedId === option.id
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400'
            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'">
          <input
            type="radio"
            :value="option.id"
            v-model="selectedId"
            class="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600">
          <span class="ms-3 text-sm text-gray-900 dark:text-gray-100">{{ option.label }}</span>
        </label>
      </div>
      <p v-if="searchQuery && filteredOptions.length === 0" class="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
        No matches found.
      </p>
    </div>

    <div v-if="options.length > 0" class="mt-6 flex justify-end space-x-3">
      <button @click="$emit('close')" class="btn btn-secondary btn-sm">
        Cancel
      </button>
      <button
        @click="handleSave"
        :disabled="!selectedId || saving"
        class="btn btn-primary btn-sm disabled:opacity-50">
        {{ saving ? 'Connecting...' : 'Connect' }}
      </button>
    </div>

    <div v-if="error" class="mt-4 p-3 text-sm text-red-700 bg-red-50 rounded-lg dark:bg-red-900/20 dark:text-red-400">
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { enableGa, enableGsc } from '~/javascript/api/websites.js'

const props = defineProps({
  type: { type: String, required: true },
  options: { type: Array, required: true },
  websiteId: { type: String, required: true },
  websiteName: { type: String, required: true }
})

const emit = defineEmits(['saved', 'close'])

const selectedId = ref(null)
const saving = ref(false)
const error = ref(null)
const searchQuery = ref('')

const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options
  const q = searchQuery.value.toLowerCase()
  return props.options.filter((o) => o.label.toLowerCase().includes(q))
})

const handleSave = async() => {
  if (!selectedId.value) return

  saving.value = true
  error.value = null

  try {
    if (props.type === 'ga') {
      await enableGa(props.websiteId, selectedId.value)
    } else {
      await enableGsc(props.websiteId, selectedId.value)
    }
    emit('saved')
  } catch (err) {
    console.error('Failed to connect:', err)
    error.value = err.response?.data?.message || 'Failed to connect. Please try again.'
  } finally {
    saving.value = false
  }
}
</script>
